/**
 * Cart adapter — bridges DyntraStore NestJS /v1/cart to the shape the Emergent
 * UI cart context and components expect: { items: [{ product, quantity, line_total }], subtotal, count }.
 *
 * NestJS cart is JWT-guarded; the UI's CartContext falls back to a local cart
 * for unauthenticated visitors, so all functions here fail fast on 401/403 and
 * let the caller decide.
 */
import { api } from "@/lib/api";
import { normalizeProduct } from "@/lib/adapters/products";

/**
 * In-memory map from the UI's product identifier (slug, falling back to DB id)
 * to the cart-item id. Refreshed on every /v1/cart response.
 */
let itemIdByProductRef = new Map();

function refKey(raw) {
  return raw.product?.slug || raw.productId || raw.product?.id;
}

function rememberItemIds(items) {
  itemIdByProductRef = new Map(
    items.map((it) => [refKey(it), it.id]).filter(([k]) => k),
  );
}

function toUiItem(raw) {
  const product = normalizeProduct({
    ...(raw.product || {}),
    id: raw.product?.id ?? raw.productId,
    price: Number(raw.priceSnap ?? raw.product?.price ?? 0),
  });
  if (!product) return null;
  const quantity = Number(raw.qty ?? 0);
  const line_total = Number(raw.priceSnap ?? product.price) * quantity;
  return {
    product,
    quantity,
    line_total,
    cart_item_id: raw.id,
  };
}

function toSummary(cart) {
  if (!cart) return { items: [], subtotal: 0, count: 0 };
  const rawItems = Array.isArray(cart.items) ? cart.items : [];
  rememberItemIds(rawItems);
  const items = rawItems.map(toUiItem).filter(Boolean);
  const subtotal = Number(cart?.pricing?.subtotal ?? cart?.subtotal ?? items.reduce((s, i) => s + i.line_total, 0));
  const count = items.reduce((n, i) => n + i.quantity, 0);
  return { items, subtotal, count };
}

/** GET /v1/cart */
export async function getCart() {
  const { data } = await api.get("/cart");
  return toSummary(data);
}

/** POST /v1/cart/items */
export async function addToCart(productId, quantity = 1) {
  const { data } = await api.post("/cart/items", { productId, qty: quantity });
  return toSummary(data);
}

async function resolveItemId(productRef) {
  if (itemIdByProductRef.has(productRef)) return itemIdByProductRef.get(productRef);
  await getCart();
  return itemIdByProductRef.get(productRef);
}

/** PATCH /v1/cart/items/:id — needs cart-item id, resolved from productId. */
export async function updateQty(productId, quantity) {
  const itemId = await resolveItemId(productId);
  if (!itemId) throw new Error("Cart item not found");
  const { data } = await api.patch(`/cart/items/${itemId}`, { qty: Math.max(1, quantity) });
  return toSummary(data);
}

/** DELETE /v1/cart/items/:id — needs cart-item id, resolved from productId. */
export async function removeItem(productId) {
  const itemId = await resolveItemId(productId);
  if (!itemId) throw new Error("Cart item not found");
  const { data } = await api.delete(`/cart/items/${itemId}`);
  return toSummary(data);
}

/** DELETE /v1/cart — clear everything */
export async function clearCart() {
  const { data } = await api.delete("/cart/items");
  return toSummary(data?.cart);
}
